/* eslint-disable @typescript-eslint/camelcase */
import React, {
  ChangeEvent,
  FC,
  FormEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import Button from "../../../../components/Button";
import ButtonPrimary from "../../../../components/ButtonPrimary";
import Modal from "../../../../components/Modal";
import { ModalHeader } from "../../../../components/Modal/ModalHeader";
import { ModalContent } from "../../../../components/Modal/ModalContent";
import { ModalFooter } from "../../../../components/Modal/ModalFooter";
import { INewSKUMappingProps } from "./types";
import FormGroup from "../../../../components/FormGroup";
import Label from "../../../../components/Label";
import Input from "../../../../components/Input";
import Select from "../../../../components/Select";
import Textarea from "../../../../components/Textarea";
import ActiveSwitch from "../ActiveSwitch";
import {
  IProduct,
  IProductLine,
} from "../../../../services/api/productLine/types";
import { createChangeEventHandler } from "../../../../utils/common";
import { PROVIDERS } from "../../../../utils/common/contants";

import "./styles.scss";

export const NewMappingModal: FC<INewSKUMappingProps> = ({
  children,
  className,
  isOpen,
  skuMapping,
  productLines,
  tenantCode,
  onSubmit,
  onDismiss,
  isLoading,
  ...props
}) => {
  const { t } = useTranslation();
  const [productLineCode, setProductLineCode] = useState("");
  const [productCode, setProductCode] = useState("");
  const [spCode, setSpCode] = useState("");
  const [skuId, setSkuId] = useState("");
  const [comments, setComments] = useState("");
  const [numberOfLicenses, setNumberOfLicenses] = useState("1");
  const [active, setActive] = useState(true);

  const filteredProductLines = useMemo(() => {
    if (tenantCode === "all") {
      return productLines;
    }

    return productLines.filter((item: IProductLine) => {
      return item.tenant_code === tenantCode;
    });
  }, [productLines, tenantCode]);

  const products = useMemo(() => {
    const selectedProductLine = filteredProductLines.find(
      (item: IProductLine) => {
        return item.code === productLineCode;
      },
    );

    if (selectedProductLine) {
      return selectedProductLine.products;
    }

    return [];
  }, [filteredProductLines, productLineCode]);

  useEffect(() => {
    if (skuMapping) {
      setProductLineCode(skuMapping?.product_line?.code || "");
      setProductCode(skuMapping.product.code);
      setSpCode(skuMapping.sp_code);
      setSkuId(skuMapping.sp_sku_code);
      setComments(skuMapping.comment);
      setActive(skuMapping.is_active);
      setNumberOfLicenses(`${skuMapping.licenses}`);
    }
  }, [skuMapping]);

  const handleSwitchChange = () => {
    setActive(!active);
  };
  const isLicensesValid = useMemo(() => {
    return Number(numberOfLicenses) > 0;
  }, [numberOfLicenses]);

  const isFormValid =
    productLineCode &&
    productCode &&
    spCode &&
    skuId &&
    comments &&
    isLicensesValid;

  const handleProductLineUpdate = (event: ChangeEvent<HTMLSelectElement>) => {
    setProductLineCode(event.target.value);
    setProductCode("");
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    onSubmit({
      is_active: active,
      product_line_code: productLineCode,
      sp_code: spCode,
      product_code: productCode,
      sp_sku_code: skuId,
      comment: comments,
      licenses: Number(numberOfLicenses),
    });
  };

  return (
    <Modal
      className={classNames("ams-new-sku-mapping-modal", className)}
      isOpen={isOpen}
      {...props}
    >
      <form
        className="new-sku-mapping-form"
        id="new-sku-mapping-form"
        onSubmit={handleSubmit}
      >
        <ModalHeader className="ams-sku-mapping-modal-header">
          {skuMapping ? (
            <>
              {t("updateSKUMappingTitle")}
              <ActiveSwitch active={active} onChange={handleSwitchChange} />
            </>
          ) : (
            t("newSKUMappingTitle")
          )}
        </ModalHeader>
        <ModalContent>
          <FormGroup>
            <Label>{t("productLine")}</Label>
            <Select
              id="new-sku-mapping-form-product-line-select"
              value={productLineCode}
              required
              disabled={!!skuMapping}
              onChange={handleProductLineUpdate}
            >
              <option value="">{t("chooseProductLine")}</option>
              {filteredProductLines.map((item: IProductLine) => {
                return (
                  <option key={item.code} value={item.code}>
                    {item.code}
                  </option>
                );
              })}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>{t("product")}</Label>
            <Select
              value={productCode}
              id="new-sku-mapping-form-product-select"
              onChange={createChangeEventHandler(setProductCode)}
              required
            >
              <option value="">{t("chooseProduct")}</option>
              {products.map((item: IProduct) => {
                return (
                  <option key={item.product_code} value={item.product_code}>
                    {item.product_code}
                  </option>
                );
              })}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>{t("subscriptionProvider")}</Label>
            <Select
              value={spCode}
              disabled={!!skuMapping}
              id="new-sku-mapping-form-provider-select"
              onChange={createChangeEventHandler(setSpCode)}
            >
              <option value="">{t("chooseSubscriptionProvider")}</option>
              {PROVIDERS.map((provider: string) => {
                return (
                  <option key={provider} value={provider}>
                    {t(provider)}
                  </option>
                );
              })}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>{t("numberOfLicenses")}</Label>
            <Input
              required
              className="ams-sku-mapping-modal__number-of-licenses"
              type="number"
              id="new-sku-mapping-form-number-of-licenses"
              value={numberOfLicenses}
              min={1}
              error={!isLicensesValid}
              errorText={t("numberOfLicensesHaveToBeMoreThan", {
                minNumberOfLicense: 0,
              })}
              onChange={createChangeEventHandler(setNumberOfLicenses)}
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("sku")}</Label>
            <Input
              type="text"
              required
              value={skuId}
              disabled={!!skuMapping}
              id="new-sku-mapping-form-sku-input"
              onChange={createChangeEventHandler(setSkuId)}
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("comments")}</Label>
            <Textarea
              id="new-sku-mapping-form-comments-input"
              required
              value={comments}
              onChange={createChangeEventHandler(setComments)}
            />
          </FormGroup>
        </ModalContent>
        <ModalFooter>
          <Button onClick={onDismiss} className="ams-modal__action">
            {t("cancel")}
          </Button>
          <ButtonPrimary
            onClick={handleSubmit}
            className="ams-modal__action"
            disabled={!isFormValid}
            isLoading={isLoading}
          >
            {skuMapping ? t("updateSKUMappingSaveBtn") : t("addSKUMapping")}
          </ButtonPrimary>
        </ModalFooter>
      </form>
    </Modal>
  );
};
